/**
 * The only place the site is allowed to state how large the expert network is.
 *
 * These counts used to live as independent string literals in eight different
 * components, which is how the site ended up simultaneously claiming 60K+,
 * 100K+, 104K and 140k+. Import from here instead of writing a number inline.
 *
 * The tiers are the vocabulary agreed for external copy. They are not
 * interchangeable, and the distinction is the whole point: "verified" on its own
 * used to mean *registered* in marketing copy and *passed government ID* in the
 * product, which is what made the old numbers indefensible. Do not reintroduce
 * a bare "verified experts" claim.
 *
 *   Registered          every account on the platform
 *   Vetted              completed onboarding / assessment
 *   Identity-verified   passed government ID and liveness
 *   Deliverable         cleared every check a specific project requires, so it
 *                       is per-project by definition and never a site-wide
 *                       number. Use the term in prose, never as a statistic.
 *
 * Figures are rounded DOWN from production so the site always understates.
 * Re-derive with the queries below and update `AS_OF` in the same commit.
 *
 *   registered         User where role='user' and deleted_at is null and banned = false
 *   vetted             ...and onboarding_flow_status = 'COMPLETED'
 *   identityVerified   ...and verification_status = 'VERIFIED'
 *   countries          count(distinct country)
 *   openOpportunities  OpportunityListing where listing_status = 'PUBLISHED'
 */

/** Month the figures below were last re-derived from production. */
export const AS_OF = "2026-07";

/** Same month, for surfaces that cite it to a reader. */
export const AS_OF_LABEL = "July 2026";

export const NETWORK_STATS = {
  /** Everyone with a Terac profile. Actual at AS_OF: 162,516. */
  registered: 160_000,
  /** Completed onboarding / assessment. Actual at AS_OF: 14,221. */
  vetted: 14_000,
  /** Passed government ID and liveness. Actual at AS_OF: 12,239. */
  identityVerified: 12_000,
  /**
   * Distinct countries represented. Actual at AS_OF: 117. Rounded down like
   * the headcounts rather than stated exactly: the exact value drifts by a
   * country or two as accounts are deleted or banned, and an exact figure that
   * drifts upward of the truth is the thing this module exists to prevent.
   */
  countries: 115,
  /** Published opportunity listings. Actual at AS_OF: 412. */
  openOpportunities: 400,
} as const;

/** `160,000+`; thousands separators plus the understating suffix. */
export function formatCount(value: number): string {
  return `${value.toLocaleString("en-US")}+`;
}

/** `160K+`; for stat pills and other tight surfaces. */
export function formatCompactCount(value: number): string {
  return value >= 1000 ? `${Math.floor(value / 1000)}K+` : `${value}+`;
}

/**
 * Ready-made phrases for the surfaces that state a count in a sentence. Kept
 * here so the tier word and its number can never drift apart in copy.
 */
export const NETWORK_COPY = {
  registeredExperts: `${formatCount(NETWORK_STATS.registered)} registered experts`,
  vettedExperts: `${formatCount(NETWORK_STATS.vetted)} vetted experts`,
  identityVerifiedExperts: `${formatCount(NETWORK_STATS.identityVerified)} identity-verified experts`,
} as const;
